import React, { useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Avatar from '@/components/People/Avatar';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { updateBibleStudySessionNote, deleteBibleStudySessionNote } from '@/data/bibleStudies';
import { updateNote, deleteNote } from '@/data/notes';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { showAlert } from '@/utils/showAlert';
import Toast from 'react-native-toast-message';

export const formatStudyDate = (date) => date
    ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not recorded';

const attributionLabel = (note) => {
    const name = [note.Author?.firstName, note.Author?.lastName].filter(Boolean).join(' ');
    if (!name) return 'Author not recorded';
    if (note.attributionSource === 'submitter') return `Session submitted by ${name}`;
    if (note.attributionSource === 'teacher') return `Taught by ${name}`;
    return name;
};

const BibleStudyNote = ({ note, kind = 'session', loggedInUser, showSessionDate = false }) => {
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(note.content);
    const canEdit = !!loggedInUser && (note.createdBy === loggedInUser.id || loggedInUser.isAdmin);
    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['bibleStudies'] });
        if (note.personID) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.peopleNotes.byPersonId(note.personID) });
        }
    };
    const save = useMutation({
        mutationFn: () => kind === 'session'
            ? updateBibleStudySessionNote({ id: note.id, content: text.trim() })
            : updateNote({ id: note.id, content: text.trim(), visibility: 'BIBLE_STUDY', isPrivate: true }),
        onSuccess: () => {
            invalidate();
            setEditing(false);
            Toast.delayShow({ type: 'success', text2: 'Note updated' });
        },
    });
    const remove = useMutation({
        mutationFn: () => kind === 'session' ? deleteBibleStudySessionNote(note.id) : deleteNote(note.id),
        onSuccess: () => {
            invalidate();
            Toast.delayShow({ type: 'success', text2: 'Note deleted' });
        },
    });
    const busy = save.isPending || remove.isPending;
    const options = canEdit ? [
        { name: 'Edit', icon: dropdownIcons.edit, onSelect: () => { setText(note.content); setEditing(true); save.reset(); } },
        {
            name: 'Delete', icon: dropdownIcons.delete, destructive: true,
            onSelect: () => showAlert({
                title: 'Delete note?', subtitle: 'This cannot be undone.',
                isDestructive: true, confirmText: 'Delete',
                onConfirm: () => remove.mutate(),
            }),
        },
    ] : [];

    return (
        <View testID={`BibleStudyNote-${note.id}`} style={styles.note}>
            {showSessionDate && note.EventInstance?.startTime && (
                <Text style={styles.sessionDate}>Session on {formatStudyDate(note.EventInstance.startTime)}</Text>
            )}
            <View style={styles.contentRow}>
                {editing ? (
                    <TextInput
                        testID={`BibleStudyNoteInput-${note.id}`}
                        accessibilityLabel="Note text"
                        style={styles.input}
                        multiline
                        value={text}
                        onChangeText={setText}
                        editable={!busy}
                        autoFocus
                    />
                ) : <Text style={styles.content} selectable>{note.content}</Text>}
                {!editing && options.length > 0 && (
                    <DropdownMenu testID={`BibleStudyNoteMenu-${note.id}`} shouldStopPropagation disabled={busy} options={options} />
                )}
            </View>
            {editing && (
                <View style={styles.actions}>
                    <Pressable testID={`BibleStudyNoteCancel-${note.id}`} accessibilityRole="button" disabled={busy} onPress={() => setEditing(false)} style={styles.action}>
                        <Text style={styles.cancelText}>Cancel</Text>
                    </Pressable>
                    <Pressable testID={`BibleStudyNoteSave-${note.id}`} accessibilityRole="button" disabled={busy || !text.trim()} onPress={() => save.mutate()} style={[styles.action, styles.save, (busy || !text.trim()) && styles.disabled]}>
                        <Text style={styles.saveText}>{save.isPending ? 'Saving...' : 'Save note'}</Text>
                    </Pressable>
                </View>
            )}
            {(save.isError || remove.isError) && <Text accessibilityRole="alert" style={styles.error}>Could not save this change. Try again.</Text>}
            <View style={styles.byline}>
                {note.Author && <Avatar profile={note.Author} size={20} />}
                <Text style={styles.author}>{attributionLabel(note)}</Text>
            </View>
            <Text style={styles.recorded}>
                Recorded {formatStudyDate(note.createdAt)}
                {note.updatedAt && note.updatedAt !== note.createdAt ? ' (edited)' : ''}
            </Text>
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    note: { gap: 8, minWidth: 0 },
    contentRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
    content: { flex: 1, fontFamily: theme.fonts.body, fontSize: 14, lineHeight: 22, color: theme.colors.text },
    sessionDate: { fontFamily: theme.fonts.bold, fontSize: 11, color: theme.colors.groups },
    byline: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    author: { flex: 1, fontFamily: theme.fonts.heading, fontSize: 12, color: theme.colors.textSecondary },
    recorded: { fontFamily: theme.fonts.body, fontSize: 11, color: theme.colors.textSecondary },
    input: {
        flex: 1, minHeight: 88, padding: 10, borderRadius: 6,
        backgroundColor: theme.colors.surfaceVariant, color: theme.colors.text,
        fontFamily: theme.fonts.body, fontSize: 14, lineHeight: 22, textAlignVertical: 'top',
    },
    actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },
    action: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: 6 },
    cancelText: { fontFamily: theme.fonts.heading, color: theme.colors.textSecondary },
    save: { backgroundColor: theme.colors.groupsContainer },
    saveText: { fontFamily: theme.fonts.bold, color: theme.colors.groups },
    disabled: { opacity: 0.5 },
    error: { fontSize: 12, color: theme.colors.error },
}));

export default BibleStudyNote;
